import {CAPACITY_FILTER_FIELD, IMAGE_HEIGHT, IMAGE_SIZE, IMAGE_TYPE, IMAGE_WIDTH, OUTDOOR_UNIT_DEFAULT_FILTERS, PRODUCT_LISTING_ONDEMANDCOLUMNS, ROOM_CAPACITY_UNIT, StateActions} from 'extensions/multi-system/data/constants';
import {eventStateChange} from 'extensions/multi-system/utils/events';
import 'extensions/ui-components/x-loader';
import 'extensions/ui-components/x-message';
import {ProductListController} from 'extensions/ui-controllers/product-list/lib/product-list';
import {defineCustomElement} from 'extensions/ui-utils';
import {LitElement, html, nothing} from 'lit';
import {unsafeHTML} from 'lit/directives/unsafe-html.js';
import stylesheet from './x-step-outdoor.scss?as=stylesheet';

export class StepOutdoor extends LitElement {
	static get styles () {
		return [stylesheet];
	}

	static get properties () {
		return {
			loading: {
				state: true,
				type: Boolean
			},
			products: {
				state: true,
				type: Array
			},
			rooms: {
				type: Array
			},
			selectedProduct: {
				attribute: 'selected-product',
				type: String
			},
			selectedRoom: {
				attribute: 'selected-room',
				type: Number
			},
			totalCapacity: {
				attribute: 'total-capacity',
				type: Number
			}
		};
	}

	get filteredMultiSystem () {
		const capacityClasses = [];

		for (const room of this.rooms) {
			capacityClasses.push(room.capacityClass);
		}

		// Sort the capacity classes - Sometimes there is a "W" or "D" in the Capacity Class
		capacityClasses.sort((a, b) => {
			const formattedA = parseFloat(a.toString()?.replace(/[^0-9]/g, ''), 10);
			const formattedB = parseFloat(b.toString()?.replace(/[^0-9]/g, ''), 10);

			if (formattedA < formattedB) {
				return -1;
			}

			if (formattedA > formattedB) {
				return 1;
			}

			return 0;
		});

		// Always return 6 elements, if there are no values, default is 0.
		return [
			capacityClasses[0] ?? '0',
			capacityClasses[1] ?? '0',
			capacityClasses[2] ?? '0',
			capacityClasses[3] ?? '0',
			capacityClasses[4] ?? '0',
			capacityClasses[5] ?? '0'
		];
	}

	get filters () {
		return [
			...OUTDOOR_UNIT_DEFAULT_FILTERS,
			{
				name: 'facets',
				value: {
					// eslint-disable-next-line camelcase
					multi_system: this.filteredMultiSystem
				}
			},
			{
				name: 'search',
				value: [
					{
						field: 'CustomField_Values:customfields:RefrigerantType',
						operator: 'EQ',
						value: this.rooms?.length > 4 ? 'R410A' : 'R32'
					}
				]
			},
			{
				name: 'imagetypes',
				value: {
					sizes: [
						IMAGE_SIZE,
						'original'
					],
					types: [IMAGE_TYPE]
				}
			}
		];
	}

	constructor () {
		super();

		// Default values

		/**
		 * @type {boolean}
		 */
		this.loading = true;

		/**
		 * @type {import('extensions/ui-controllers/product-list/lib/product-list').MivaProduct[]}
		 */
		this.products = [];

		/**
		 * @type {import('extensions/multi-system/types').Room[]}
		 */
		this.rooms = [];

		/**
		 * @type {string}
		 */
		this.selectedProduct = '';

		/**
		 * @type {number}
		 */
		this.selectedRoom = 0;

		/**
		 * @type {number}
		 */
		this.totalCapacity = 0;
	}

	async getProducts () {
		await this.updateComplete;

		this.loading = true;

		// Get products
		const productListController = new ProductListController(this, {
			cache: true,
			count: 0,
			filters: this.filters,
			onDemandColumns: [
				...PRODUCT_LISTING_ONDEMANDCOLUMNS,
				'category'
			]
		});

		this.products = await productListController.getProducts();

		// Sort products by capacity
		this.products.sort((a, b) => parseFloat(a?.CustomField_Values?.customfields?.[CAPACITY_FILTER_FIELD]) - parseFloat(b?.CustomField_Values?.customfields?.[CAPACITY_FILTER_FIELD]));

		this.loading = false;
	}

	handleProductClick (e, product) {
		this.dispatchEvent(eventStateChange({
			bubbles: true,
			composed: true,
			detail: {
				action: StateActions.OutdoorProductUpdate,
				merge: true,
				payload: product?.code
			}
		}));
	}

	updated (changedProperties) {
		if (changedProperties.has('selectedRoom')) {
			this.getProducts();
		}
	}

	/**
	 * @param {import('extensions/ui-controllers/product-list').MivaProduct} product
	 */
	renderInventoryMessage (product) {
		if (product.inventory_display_html) {
			return html`
				<div slot="inventory">
					${unsafeHTML(product.inventory_display_html)}
				</div>
			`;
		}

		return nothing;
	}

	renderProducts () {
		if (this.loading) {
			return html`<x-loader class="loader" aspect-ratio-desktop="8/2" aspect-ratio-tablet="8/2" aspect-ratio="8/2"></x-loader>`;
		}

		return html`
			<div class="products">
				${this.products?.map(product => html`
					<x-selectable-card ?selected="${this.selectedProduct === product.code}" @click="${e => this.handleProductClick(e, product)}">
						<x-product-card
							class="product"
							show-details-link
							base-price="${product.base_price}"
							capacity-cooling="${product?.CustomField_Values?.customfields?.CapacityCoolDisplaykW}"
							capacity-heating="${product?.CustomField_Values?.customfields?.CapacityHeatDisplaykW}"
							category="${product.category?.name}"
							formatted-price="${product.formatted_price}"
							formatted-base-price="${product.formatted_base_price}"
							image-height="${IMAGE_HEIGHT}"
							image-url="${product?.imagetypes[IMAGE_TYPE]?.sizes[IMAGE_SIZE]?.url || product?.imagetypes[IMAGE_TYPE]?.sizes?.original?.url}"
							image-width="${IMAGE_WIDTH}"
							price="${product.price}"
							sku="${product.code}"
							url="${product.url}"
							?hide-pricing="${!window.mivaJS?.Customer_Permissions?.WebstorePricing}"
						>
							${this.renderInventoryMessage(product)}
						</x-product-card>
					</x-selectable-card>
				`)}
			</div>
		`;
	}

	render () {
		return html`
			<div class="container">
				${this.products?.length === 0 && this.loading === false ? html`<x-message type="error">The indoor units you selected exceed the total maximum capacity. Modify your selections to find a compatible outdoor unit.</x-message>` : nothing}
				<x-section>
					<div slot="heading">
						Select an Outdoor Unit
						<div class="subheading">
							Total Selected Indoor Capacity: ${this.totalCapacity}${ROOM_CAPACITY_UNIT}
						</div>
					</div>

					${this.renderProducts()}
				</x-section>
			</div>
		`;
	}
};

defineCustomElement('x-step-outdoor', StepOutdoor);
